// deployment_class: customer_env_allowed
/**
 * LandingVennView — the VENN view: which projects sit in more than one
 * call-on-your-attention set.
 *
 * Three sets — Needs attention · Board action · Money at risk — and every
 * membership is COMPUTED by predicate over the project's own record (see
 * landingSchemaViews' VENN_SETS). Nothing is hand-listed, so a deal that stops
 * breaching a floor leaves the set by itself.
 *
 * The overlaps carry the message: a project in all three is the one thing that
 * needs the CEO today, and a project outside every circle is the one that needs
 * nothing. Both readings are impossible to miss here and easy to miss in a list.
 */
import { useMemo, useState } from 'react';
import { SV_STAGE_H, SV_STAGE_W, layoutVenn, type VennMember } from './landingSchemaViews';
import { type NodeIndex } from './landingDrillData';
import { LandingDetailPopover, type DetailAnchor, type DetailPayload } from './LandingDetailPopover';

type CSSVars = React.CSSProperties & Record<string, string | number>;

export function LandingVennView({ index }: { index: NodeIndex }): JSX.Element {
  const { sets, regions } = useMemo(() => layoutVenn(index), [index]);
  const [detail, setDetail] = useState<{ payload: DetailPayload; anchor: DetailAnchor } | null>(
    null,
  );

  const setLabel = (id: string): string => sets.find((s) => s.id === id)?.label ?? id;

  const openMember = (m: VennMember, el: HTMLElement): void => {
    const r = el.getBoundingClientRect();
    const metrics = m.metrics;
    const inSets = m.setIds.length
      ? m.setIds.map(setLabel).join(' · ')
      : 'none of the three — nothing here is waiting on you';
    setDetail({
      payload: {
        kindLabel: m.setIds.length === sets.length ? 'In every set' : 'Partnership',
        label: m.name,
        value: inSets,
        detail: `${metrics.structure} with ${metrics.developer}. $${metrics.tdc}M across ${metrics.units} units. ${metrics.blurb}`,
        status: metrics.status,
      },
      anchor: { top: r.top, left: r.left, width: r.width, height: r.height },
    });
  };

  return (
    <>
      <svg
        className="kh-sv-venn"
        viewBox={`0 0 ${SV_STAGE_W} ${SV_STAGE_H}`}
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        {sets.map((s, i) => (
          <circle key={s.id} className={`kh-sv-venn-circle c${i}`} cx={s.cx} cy={s.cy} r={s.r} />
        ))}
      </svg>

      {sets.map((s, i) => (
        <span
          key={s.id}
          className={`kh-sv-venn-title c${i}`}
          style={{ left: `${s.labelX}px`, top: `${s.labelY}px` } as CSSVars}
          aria-hidden="true"
        >
          {s.label}
          <b>{s.count}</b>
        </span>
      ))}

      {/* Name the outside region. A chip sitting alone off the diagram reads as a
          layout accident; labelled, it is the readout that matters most on a good
          day — this one needs nothing from you. */}
      {regions
        .filter((r) => r.setIds.length === 0 && r.members.length > 0)
        .map((r) => (
          <span
            key="outside-caption"
            className="kh-sv-venn-outside-cap"
            style={{ left: `${r.x}px`, top: `${r.y - 30}px` } as CSSVars}
            aria-hidden="true"
          >
            Outside all three — nothing waiting on you
          </span>
        ))}

      {regions.map((region) =>
        region.members.map((m, i) => (
          <button
            type="button"
            key={m.id}
            className={`kh-sv-venn-chip st-${m.metrics.status}${region.setIds.length === sets.length ? ' is-all' : ''}${region.setIds.length === 0 ? ' is-outside' : ''}`}
            style={
              {
                left: `${region.x}px`,
                // Stack multiple members in one region without overlapping.
                top: `${region.y + (i - (region.members.length - 1) / 2) * 34}px`,
              } as CSSVars
            }
            onClick={(e) => openMember(m, e.currentTarget)}
            aria-label={
              region.setIds.length
                ? `${m.name} — in ${region.setIds.map(setLabel).join(' and ')}`
                : `${m.name} — in none of the three sets`
            }
          >
            {m.name}
          </button>
        )),
      )}

      {/* The rules, stated. A Venn whose membership rule is invisible is a picture;
          stated, it is a claim the viewer can check. */}
      <div className="kh-sv-venn-rules">
        {sets.map((s, i) => (
          <span key={s.id} className={`kh-sv-venn-rule c${i}`}>
            <i aria-hidden="true" />
            <b>{s.label}</b> — {s.rule}
          </span>
        ))}
      </div>

      {detail && (
        <LandingDetailPopover
          payload={detail.payload}
          anchor={detail.anchor}
          onClose={() => setDetail(null)}
        />
      )}
    </>
  );
}
